import { useEffect, useMemo, useState } from 'react'

import {
  F0Box,
  SurveyFormBuilder,
  SurveyFormBuilderElement,
  QuestionType,
} from '@factorialco/f0-react'
import { FormDetailFragment } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useOne } from 'modules/ai/lib/useOne'
import {
  FromFormToQuestionnaire,
  FromQuestionaireToMutationVariables,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { FormEditorActions } from './formEditorActions'
import { AgentStateType, QuestionnaireType } from './types'

function getSectionsCount(questionnaire: QuestionnaireType): number {
  return questionnaire.elements.filter((element) => element.type === 'section').length
}

function getQuestionsCount(questionnaire: QuestionnaireType): number {
  const rootQuestions = questionnaire.elements.filter(
    (element) => element.type !== 'section'
  ).length
  const sectionQuestions = questionnaire.elements
    .filter((element) => element.type === 'section')
    .flatMap((section) => section.elements.length)
  return rootQuestions + sectionQuestions.reduce((acc, curr) => acc + curr, 0)
}

interface FormEditorProps {
  form: FormDetailFragment | null
  initialQuestionnaire: QuestionnaireType
  resourceType: string
  formType?: AgentStateType['formType']
  allowedQuestionTypes?: QuestionType[]
}

export const FormEditor = ({
  form,
  initialQuestionnaire,
  formType,
  allowedQuestionTypes,
}: FormEditorProps) => {
  const { updateHeader, updateMetadataProperty } = useResourceHeader()
  const { updateForm } = useUpdateNewForm()

  const [savedState, setSavedState] = useState<QuestionnaireType>(initialQuestionnaire)

  const initialAgentState: AgentStateType = useMemo(
    () => ({
      formType,
      questionnaire: initialQuestionnaire,
      updateForm: true,
    }),
    [formType, initialQuestionnaire]
  )

  const oneOptions = {
    autoOpenChat: true as const,
    initialMessage: i18n.t('forms.one.chat_initial_message'),
    fullscreen: false,
  }

  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<AgentStateType>(['surveys'], initialAgentState, oneOptions)

  useEffect(() => {
    if (!agentState?.questionnaire) return
    updateHeader((header) => ({
      ...(header || {}),
      title: agentState.questionnaire.title,
      description: agentState.questionnaire.description,
    }))
    updateMetadataProperty(i18n.t('surveys.header.metadata.questions'), {
      type: 'text' as const,
      content: `${getQuestionsCount(agentState.questionnaire)}`,
    })
    updateMetadataProperty(i18n.t('surveys.header.metadata.sections'), {
      type: 'text' as const,
      content: `${getSectionsCount(agentState.questionnaire)}`,
    })
  }, [agentState?.questionnaire, updateHeader, updateMetadataProperty])

  const isDirty = useMemo(() => {
    if (!agentState?.questionnaire) return false
    return !isEqual(agentState.questionnaire, savedState)
  }, [agentState?.questionnaire, savedState])

  const onDiscard = () => {
    if (agentState && setAgentState) {
      setAgentState({ ...agentState, questionnaire: initialQuestionnaire, updateForm: true })
      setSavedState(initialQuestionnaire)
    }
  }

  const onSave = async () => {
    if (!form || !agentState?.questionnaire || !agentState || !setAgentState) return

    const result = await updateForm({
      ...FromQuestionaireToMutationVariables(form.id, form.anonymous, agentState.questionnaire),
    })
    if (result.success) {
      setSavedState(agentState.questionnaire)
      setAgentState({
        ...agentState,
        questionnaire: agentState.questionnaire,
        updateForm: true,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.surveys.create.edit_modal.form_success_message'),
        time: 1000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
      })
    }
  }

  const handleFormChange = (updatedElements: SurveyFormBuilderElement[]) => {
    if (!agentState || !setAgentState) return
    const updatedQuestionnaire: QuestionnaireType = {
      ...agentState.questionnaire,
      elements: FromFormToQuestionnaire(updatedElements),
    }
    setAgentState({
      ...agentState,
      questionnaire: updatedQuestionnaire,
    })
  }

  if (!agentState) {
    return <Loading expand />
  }

  const formIsDraft = form?.status === 'draft'
  const elements = FromQuestionnaireToForm(agentState.questionnaire.elements)

  return (
    <F0Box width="full" height="full" padding="md" paddingBottom="5xl">
      <SurveyFormBuilder
        disabled={!formIsDraft}
        elements={elements}
        onChange={handleFormChange}
        allowedQuestionTypes={allowedQuestionTypes}
        applyingChanges={isThinking}
      />
      {formIsDraft && !isThinking && (
        <FormEditorActions isDirty={isDirty} onSave={onSave} onDiscard={onDiscard} />
      )}
    </F0Box>
  )
}
